import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './ThirdFlows.styled';
import ThirdAnalysesList from 'components/Analyses/ThirdAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useThirdFlows from 'hooks/useThirdFlows';
import useThirdAnalyses from 'hooks/useThirdAnalyses';
import { DELETE_THIRD_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { thirdFlowsSelectors } from "features/thirdFlows";

const ThirdFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getThirdFlow, createThirdFlow, updateThirdFlow } = useThirdFlows();
  const { getThirdAnalyses, clearLocalThirdAnalyses } = useThirdAnalyses();
  const id = useCustomSelector(thirdFlowsSelectors.getThirdFlowId);
  const thirdFlow = useCustomSelector(thirdFlowsSelectors.getThirdFlowData);
  const fetching = useCustomSelector(thirdFlowsSelectors.thirdFlowIsFetching);
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (third_flow) => {
    setPrompt({ value: third_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const third_flow = {
      ...(needToUpdateField(thirdFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(third_flow)) {
      if (!!id) {
        updateThirdFlow(third_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createThirdFlow(third_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_THIRD_FLOW });
  const handleCopy = () => {
    if (!!thirdFlow?.email && !copied)
    {
      navigator.clipboard.writeText(thirdFlow.email);
      setCopied(true);
    }
  };
  const fetchThirdAnalyses = useCallback(() => {
    getThirdAnalyses({ offset: 0 })
  }, [getThirdAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalThirdAnalyses();
    };
  }, [id, clearLocalThirdAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getThirdFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { third_flow } = payload;
          handleEntering(third_flow);
          fetchThirdAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getThirdFlow, fetchThirdAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Tooltip 
              title={!!thirdFlow?.email ? (copied ? 'Copied!' : 'Click to copy') : ''}
              followCursor
              onClose={() => setTimeout(() => setCopied(false), 300)} 
            >
              <Typography 
                variant='subtitle2'
                component='div' 
                sx={{ wordBreak: 'break-all', cursor: 'pointer' }}
                onClick={handleCopy}
              >
                {`Receiving Email: ${!!thirdFlow?.email ? thirdFlow.email : '-'}`}
              </Typography>
            </Tooltip>
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <ThirdAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ThirdFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);